/**
 * Pricing-tier windows and format status (A-01). Pure, so the back office can warn before
 * saving and the Worker enforces the same rules.
 */
type When = string | Date | null;
export type TierWindow = { id?: string; startsAt: When; endsAt: When };

const ms = (d: When, open: number) => (d === null ? open : new Date(d).getTime());

/** Windows are [start, end); a null start or end is open-ended. */
export function windowsOverlap(a: TierWindow, b: TierWindow): boolean {
  return (
    ms(a.startsAt, -Infinity) < ms(b.endsAt, Infinity) &&
    ms(b.startsAt, -Infinity) < ms(a.endsAt, Infinity)
  );
}

/** The first other tier (by id) whose window overlaps `candidate`'s. */
export function overlappingTier<T extends TierWindow>(candidate: TierWindow, others: T[]) {
  return others.find((t) => t.id !== candidate.id && windowsOverlap(candidate, t));
}

/** On sale at `now`. */
export const isLiveTier = (t: TierWindow, now: Date) =>
  ms(t.startsAt, -Infinity) <= now.getTime() && now.getTime() < ms(t.endsAt, Infinity);

/** Ids in sale order (start, then end); `sort_order` follows it so the last tier is the standard price. */
export function chronologicalIds(tiers: Required<TierWindow>[]): string[] {
  return [...tiers]
    .sort(
      (a, b) =>
        ms(a.startsAt, -Infinity) - ms(b.startsAt, -Infinity) ||
        ms(a.endsAt, Infinity) - ms(b.endsAt, Infinity),
    )
    .map((t) => t.id!);
}

export type FormatStatus = 'draft' | 'open' | 'closed' | 'sold_out';

/** Columns to write for a status: only `open` formats are listed and sold (`is_active`). */
export const formatStatusColumns = (status: FormatStatus) => ({
  status,
  isActive: status === 'open',
});

/**
 * Status of a stored row. Rows deactivated before `status` existed have `is_active = false`
 * with the column default `open`: they read as `draft`, which is what they were.
 */
export const effectiveFormatStatus = (f: { status: FormatStatus; isActive: boolean }) =>
  !f.isActive && f.status === 'open' ? 'draft' : f.status;
